import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import Weather from './Weather';
import { useTranslation } from 'react-i18next';
import { getLanguageKeys } from 'utils/methods';

const Navbar = () => {  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const location = useLocation();
  const { user, logout, isAuthenticated, openLoginModal } = useAuth();
  const { i18n, t } = useTranslation();
  const userMenuRef = useRef(null);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'english');
  const isLanguageFetched = useRef(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleLogout = async () => {
    await logout();
    setIsUserMenuOpen(false);
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Show weather widget only on safety-now pages
  const showWeatherWidget = location.pathname.includes('safety-now');

  // Fetch available languages from backend and cache locally
  useEffect(() => {
    // Prevent duplicate fetching in React.StrictMode
    if (isLanguageFetched.current) return;
    
    const cached = localStorage.getItem('available_languages');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        setAvailableLanguages(parsed);
        isLanguageFetched.current = true;
      } catch (e) {
        localStorage.removeItem('available_languages');
      }
    }

    const fetchLanguages = async () => {
      isLanguageFetched.current = true;
      try {
        const res = await getLanguageKeys();
        const data = res.data;
        if (Array.isArray(data)) {
          setAvailableLanguages(data);
          localStorage.setItem('available_languages', JSON.stringify(data));
        }
      } catch (err) {
        console.error('Error fetching languages:', err);
        // silent fail — keep cached or default
      }
    };

    if (!cached) fetchLanguages();
  }, []);

  // when language changes locally, persist and tell i18n
  useEffect(() => {
    localStorage.setItem('language', language);
    const keyToCode = { english: 'en', hindi: 'hi' };
    const code = keyToCode[language] || 'en';
    i18n.changeLanguage(code);
    localStorage.setItem('languageCode', code);
  }, [language, i18n]);

  return (
    <nav className="bg-white shadow-lg sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img src="/iitm_logo.png" alt="ChemSafe Logo" className="h-10 w-auto" />
            <img src="/logo3.png" alt="ChemSafe Logo" className="h-10 w-auto" />
            <span className="text-xl font-bold text-gray-800">ChemSafe</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`transition-colors duration-200 ${
                isActive('/') 
                  ? 'text-blue-600 font-semibold border-b-2 border-blue-600' 
                  : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              {t('navbar.home')}
            </Link>
            <Link
              to="/tools"
              className={`transition-colors duration-200 ${
                location.pathname.startsWith('/tools') 
                  ? 'text-blue-600 font-semibold border-b-2 border-blue-600' 
                  : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              {t('navbar.tools')}
            </Link>
            <Link
              to="/survey"
              className={`transition-colors duration-200 ${
                isActive('/survey') 
                  ? 'text-blue-600 font-semibold border-b-2 border-blue-600' 
                  : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              {t('navbar.survey')}
            </Link>
            <Link
              to="/about"
              className={`transition-colors duration-200 ${
                isActive('/about') 
                  ? 'text-blue-600 font-semibold border-b-2 border-blue-600' 
                  : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              {t('navbar.about')}
            </Link>            {/* Weather Widget - Only show on safety-now pages */}
            {showWeatherWidget && (
              <div className="ml-4">
                <Weather />
              </div>
            )}
            {/* Language selector */}
            <div className="ml-4">
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="border border-gray-200 rounded-md px-2 py-1 text-sm"
                aria-label="Select language"
              >
                <option value="english">English</option>
                <option value="hindi">Hindi</option>
                {availableLanguages && availableLanguages.length > 0 && availableLanguages.map((lg, idx) => {
                  if (lg === 'english' || lg === 'hindi') return null;
                  const label = lg.split('_').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
                  return (<option key={idx} value={lg}>{label}</option>);
                })}
              </select>
            </div>            {/* Authentication */}
            {isAuthenticated ? (
              <div className="relative ml-4" ref={userMenuRef}>
                <button
                  onClick={toggleUserMenu}
                  className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 focus:outline-none"
                >
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {user?.full_name?.charAt(0)?.toUpperCase() || user?.username?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <span className="text-sm font-medium">{user?.full_name || user?.username}</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      {t('navbar.profile')}
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      {t('navbar.logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (              <div className="ml-4 space-x-4">
                <button
                  onClick={() => openLoginModal()}
                  className="text-gray-700 hover:text-blue-600 font-medium"
                >
                  {t('navbar.login')}
                </button>
                <Link
                  to="/register"
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                >
                  {t('navbar.register')}
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="text-gray-700 hover:text-blue-600 focus:outline-none focus:text-blue-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t">
              <Link
                to="/"
                className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${
                  isActive('/') 
                    ? 'text-blue-600 bg-blue-50' 
                    : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('navbar.home')}
              </Link>
              <Link
                to="/tools"
                className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${
                  location.pathname.startsWith('/tools') 
                    ? 'text-blue-600 bg-blue-50' 
                    : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('navbar.tools')}
              </Link>
              <Link
                to="/survey"
                className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${
                  isActive('/survey') 
                    ? 'text-blue-600 bg-blue-50' 
                    : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('navbar.survey')}
              </Link>
              <Link
                to="/about"
                className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${
                  isActive('/about') 
                    ? 'text-blue-600 bg-blue-50' 
                    : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('navbar.about')}
              </Link>
              <Link
                to="/resources"
                className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${
                  isActive('/resources') 
                    ? 'text-blue-600 bg-blue-50' 
                    : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('navbar.resources')}
              </Link>              {/* Weather Widget in Mobile Menu - Only show on safety-now pages */}
              {showWeatherWidget && (
                <div className="px-3 py-2">
                  <Weather />
                </div>
              )}

              {/* Authentication in Mobile Menu */}
              {isAuthenticated ? (
                <>
                  <div className="px-3 py-2 border-t border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-medium">
                          {user?.full_name?.charAt(0)?.toUpperCase() || user?.username?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-gray-700">
                        {user?.full_name || user?.username}
                      </span>
                    </div>
                  </div>                  <Link
                    to="/profile"
                    className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {t('navbar.profile')}
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50"
                  >
                    {t('navbar.logout')}
                  </button>
                </>
              ) : (
                <>                  <div className="border-t border-gray-200"></div>
                  <button
                    onClick={() => {
                      openLoginModal();
                      setIsMobileMenuOpen(false);
                    }}
                    className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 w-full text-left"
                  >
                    {t('navbar.login')}
                  </button>
                  <Link
                    to="/register"
                    className="block px-3 py-2 rounded-md text-base font-medium bg-blue-600 text-white hover:bg-blue-700 mx-3"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {t('navbar.register')}
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
